<script lang="ts">
  import { onMount } from 'svelte';
  import { generateTOTP, splitCode } from '$crypto/totp';
  import Icon from '../icons/Icon.svelte';
  import ShaderCanvas from '../effects/ShaderCanvas.svelte';
  import crystalShader from '$lib/shaders/crystal-ring.wgsl?raw';
  import { ticker } from '$stores/ticker.svelte';
  import { resolveIdentity } from '$utils/identity';

  const DEMO_SECRET = 'JBSWY3DPEHPK3PXP';
  const RING_RADIUS = 100;
  const RING_LEN = 2 * Math.PI * RING_RADIUS;
  const githubIdentity = resolveIdentity('GitHub');

  let shaderTime = $state(0);
  let mounted = $state(false);
  let animId = 0;

  onMount(() => {
    mounted = true;
    const start = performance.now();
    function tick() {
      shaderTime = (performance.now() - start) / 1000;
      animId = requestAnimationFrame(tick);
    }
    tick();
    return () => cancelAnimationFrame(animId);
  });

  let codeFront = $state('— — —');
  let codeBack = $state('— — —');
  let remaining = $state(30);
  let warn = $state(false);

  const crystalUniforms = $derived(
    new Float32Array([shaderTime, 1 - remaining / 30, warn ? 1 : 0, 0])
  );

  // Recompute on every ticker tick. Single shared interval.
  // Read ticker.now into a local AND reference it so production minification
  // can't dead-code-eliminate the reactive subscription.
  $effect(() => {
    const tick = ticker.now;
    if (tick < 0) return;
    let cancelled = false;
    generateTOTP(DEMO_SECRET).then((r) => {
      if (cancelled) return;
      const [a, b] = splitCode(r.code);
      codeFront = a;
      codeBack = b;
      remaining = r.remaining;
      warn = r.remaining <= 8;
    });
    return () => {
      cancelled = true;
    };
  });

  const dashOffset = $derived(RING_LEN * (1 - remaining / 30));
</script>

<div class="hero-showcase">
  <div class="proof-badge" aria-label="Generated locally">generated locally · 0ms ago</div>
  <article class="showcase-card" aria-label="Live VuAuth code preview">
    <div class="showcase-row">
      <div
        class="showcase-mono"
        style:background={githubIdentity.bg}
        style:color={githubIdentity.fg}
        aria-hidden="true"
      >
        {githubIdentity.monogram}
      </div>
      <div class="showcase-meta">
        <div class="showcase-issuer">GitHub</div>
        <div class="showcase-account">[email protected]</div>
      </div>
      <div class="showcase-lock" title="Encrypted at rest">
        <Icon name="lock" size={14} />
      </div>
    </div>
    <div class="crystal" aria-live="polite">
      <div class="crystal-ring" aria-hidden="true">
        {#if mounted}
          <div class="crystal-shader-layer">
            <ShaderCanvas shader={crystalShader} uniforms={crystalUniforms}>
              {#snippet fallback()}<span></span>{/snippet}
            </ShaderCanvas>
          </div>
        {/if}
        <svg viewBox="0 0 220 220">
          <circle class="crystal-ring-bg" cx="110" cy="110" r={RING_RADIUS} />
          <circle
            class="crystal-ring-fg"
            class:warn
            cx="110"
            cy="110"
            r={RING_RADIUS}
            stroke-dasharray={RING_LEN}
            stroke-dashoffset={dashOffset}
          />
        </svg>
      </div>
      <div class="crystal-code" class:warn>
        <span class="crystal-code-grp">{codeFront}</span>
        <span class="crystal-code-grp">{codeBack}</span>
      </div>
      <div class="crystal-sub"><span>{remaining}</span>s · refreshes locally</div>
    </div>
    <div class="showcase-foot">
      <span class="showcase-foot-grp">
        <Icon name="shield" size={13} />
        <strong>Level 0</strong> verified
      </span>
      <span class="showcase-foot-grp">
        <Icon name="check" size={13} />
        Free · forever
      </span>
    </div>
  </article>
  <div class="detective-hint" role="note">
    <Icon name="search" size={14} />
    <span>Site Detective matched <code>github.com</code></span>
  </div>
</div>

<style>
  .hero-showcase {
    position: relative;
    display: grid;
    place-items: center;
    min-height: 460px;
  }
  .showcase-card {
    position: relative;
    width: 100%;
    max-width: 380px;
    background: var(--bg-card);
    border: 1px solid var(--border-2);
    border-radius: var(--radius-lg);
    padding: 28px 24px 24px;
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    box-shadow: var(--shadow);
  }
  :global([data-theme='modern']) .showcase-card {
    background: linear-gradient(
      180deg,
      color-mix(in oklab, var(--text) 4%, transparent),
      color-mix(in oklab, var(--text) 2%, transparent)
    );
  }
  :global([data-theme='brutalist']) .showcase-card {
    border: 3px solid var(--text);
    box-shadow: 12px 12px 0 var(--text);
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
  }
  .showcase-row {
    display: flex;
    align-items: center;
    gap: 14px;
    margin-bottom: 22px;
  }
  .showcase-mono {
    width: 44px;
    height: 44px;
    border-radius: 10px;
    display: grid;
    place-items: center;
    font-weight: 800;
    font-size: 18px;
    letter-spacing: -0.04em;
  }
  :global([data-theme='brutalist']) .showcase-mono {
    border-radius: 0;
    border: 2px solid var(--text);
  }
  .showcase-meta {
    flex: 1;
    min-width: 0;
  }
  .showcase-issuer {
    font-size: 15px;
    font-weight: 700;
    color: var(--text);
    letter-spacing: -0.01em;
  }
  .showcase-account {
    font-size: 12px;
    color: var(--text-3);
    margin-top: 2px;
  }
  .showcase-lock {
    width: 30px;
    height: 30px;
    border-radius: 8px;
    display: grid;
    place-items: center;
    background: var(--accent-dim);
    color: var(--accent);
  }
  :global([data-theme='brutalist']) .showcase-lock {
    border-radius: 0;
    border: 2px solid var(--text);
    color: var(--text);
  }
  .crystal {
    position: relative;
    display: grid;
    place-items: center;
    width: 100%;
    padding: 28px 0 24px;
  }
  .crystal-ring {
    position: absolute;
    left: 50%;
    top: 50%;
    width: 220px;
    height: 220px;
    transform: translate(-50%, -50%);
  }
  .crystal-shader-layer {
    position: absolute;
    inset: 0;
    z-index: 1;
    border-radius: 50%;
    overflow: hidden;
    pointer-events: none;
  }
  .crystal-ring svg {
    width: 100%;
    height: 100%;
    transform: rotate(-90deg);
  }
  .crystal-ring-bg {
    fill: none;
    stroke: var(--border);
    stroke-width: 2;
  }
  :global([data-theme='brutalist']) .crystal-ring-bg {
    stroke: var(--text);
    stroke-width: 3;
    opacity: 0.15;
  }
  .crystal-ring-fg {
    fill: none;
    stroke: var(--accent);
    stroke-width: 2;
    stroke-linecap: round;
    transition:
      stroke-dashoffset 1s linear,
      stroke 220ms;
  }
  :global([data-theme='brutalist']) .crystal-ring-fg {
    stroke: var(--text);
    stroke-width: 4;
    stroke-linecap: butt;
    transition: stroke-dashoffset 1s linear;
  }
  .crystal-ring-fg.warn {
    stroke: var(--warn);
  }
  .crystal-code {
    position: relative;
    z-index: 2;
    font-family: var(--font-mono);
    font-size: 38px;
    font-weight: 700;
    letter-spacing: 0.12em;
    color: var(--text);
    display: flex;
    gap: 14px;
    user-select: none;
  }
  :global([data-theme='brutalist']) .crystal-code {
    font-weight: 700;
    letter-spacing: 0.08em;
  }
  .crystal-code.warn {
    color: var(--warn);
  }
  .crystal-sub {
    position: relative;
    z-index: 2;
    margin-top: 14px;
    font-family: var(--font-mono);
    font-size: 11px;
    color: var(--text-3);
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }
  .showcase-foot {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-top: 18px;
    border-top: 1px solid var(--border);
    font-size: 12px;
    color: var(--text-3);
  }
  :global([data-theme='brutalist']) .showcase-foot {
    border-top: 2px solid var(--text);
  }
  .showcase-foot-grp {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .showcase-foot strong {
    color: var(--good);
    font-weight: 700;
  }
  :global([data-theme='brutalist']) .showcase-foot strong {
    color: var(--text);
    background: var(--good);
    padding: 1px 6px;
  }
  .proof-badge {
    position: absolute;
    right: -8px;
    top: -16px;
    z-index: 3;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 12px;
    background: var(--bg);
    border: 1px solid var(--border-2);
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;
    color: var(--good);
    letter-spacing: 0.04em;
  }
  .proof-badge::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--good);
    box-shadow: 0 0 8px var(--good);
    animation: pulse 2s ease-in-out infinite;
  }
  :global([data-theme='brutalist']) .proof-badge {
    border-radius: 0;
    border: 2px solid var(--text);
    box-shadow: 3px 3px 0 var(--text);
    background: var(--good);
    color: var(--text);
    font-weight: 800;
  }
  :global([data-theme='brutalist']) .proof-badge::before {
    border-radius: 0;
    box-shadow: none;
    background: var(--text);
  }
  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.45;
    }
  }
  .detective-hint {
    position: absolute;
    left: -24px;
    bottom: 18px;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 14px;
    background: var(--bg-card);
    border: 1px solid var(--border-2);
    border-radius: var(--radius);
    font-size: 12px;
    color: var(--text-2);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
  }
  :global([data-theme='brutalist']) .detective-hint {
    border: 2px solid var(--text);
    box-shadow: 4px 4px 0 var(--text);
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
    font-weight: 700;
  }
  .detective-hint :global(svg) {
    color: var(--accent);
  }
  :global([data-theme='brutalist']) .detective-hint :global(svg) {
    color: var(--text);
  }
  .detective-hint code {
    font-family: var(--font-mono);
    color: var(--text);
    font-size: 11px;
  }
  @media (max-width: 540px) {
    .detective-hint {
      display: none;
    }
    .proof-badge {
      right: 8px;
      top: -12px;
    }
  }
</style>
